"use client"
import { Tooltip as TooltipPrimitive } from "@base-ui-components/react/tooltip"
import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react"
import {
  TooltipVariantProps,
  tooltipVariants,
} from "../../styles/utils/tooltip"
import { classNames } from "../../utils"
import { Text } from "../Text"

export const TooltipLabel = forwardRef<
  ElementRef<typeof Text.Label>,
  ComponentPropsWithoutRef<typeof Text.Label>
>(function TooltipLabel({ className, children, ...rest }, ref) {
  return (
    <Text.Label
      asChild={true}
      className={classNames("normal-case", className)}
      ref={ref}
      size="sm"
      {...rest}
    >
      <p>{children}</p>
    </Text.Label>
  )
})

export type TooltipProps = Pick<
  ComponentProps<typeof TooltipPrimitive.Root>,
  "open" | "defaultOpen" | "onOpenChange" | "delay" | "hoverable"
> &
  Pick<
    ComponentProps<typeof TooltipPrimitive.Positioner>,
    | "align"
    | "side"
    | "alignOffset"
    | "sideOffset"
    | "anchor"
    | "className"
    | "style"
  > &
  Pick<ComponentProps<typeof TooltipPrimitive.Portal>, "container"> &
  TooltipVariantProps & {
    /**
     * Trigger content
     */
    children: React.ReactNode &
      ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>["render"]
    /**
     * Content to render in the portal.
     */
    content: React.ReactNode

    /**
     * Whether to disable the Tooltip from opening.
     */
    disabled?: boolean

    /**
     * Whether to disable pointer events on the Tooltip.
     * We need to apply classes to [data-radix-popper-content-wrapper] to make this work.
     * NOTE: this automatically disables portals
     */
    disablePointerEvents?: boolean

    /**
     * This will make the tooltip stick to the cursor as it moves.
     */
    attachToCursor?: boolean

    /**
     * Set the cursor class name
     * Default: cursor-pointer
     */
    cursorClassName?: string
  }

/**
 * A popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it.
 */
export const TooltipBase = forwardRef<
  ElementRef<typeof TooltipPrimitive.Popup>,
  TooltipProps
>(function TooltipBase(
  {
    open,
    defaultOpen,
    onOpenChange,
    content,
    children,
    className,
    disabled,
    delay = 100,
    hoverable,
    align = "center",
    alignOffset = 0,
    sideOffset = 8,
    animation = "fade-in",
    disablePointerEvents,
    attachToCursor,
    side = "top",
    style,
    anchor,
    container,
    cursorClassName = "cursor-pointer",
  },
  ref,
) {
  // Keeping track of the open state internally as there can be issues with uncontrolled mode when tracking cursor axis
  // https://github.com/mui/base-ui/issues/1447#issuecomment-2673177971
  const [openedState, setOpenedState] = useState(Boolean(defaultOpen ?? open))
  const resolvedOpenState = open === undefined ? openedState : open
  const triggerRef = useRef<HTMLElement | null>(null)

  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      setOpenedState(open)
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  useEffect(() => {
    // Extra safe guard to close the tooltip when the mouse moves outside of the trigger element
    const handleMouseMove = (e: MouseEvent) => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const isOutside =
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom

        if (isOutside) {
          handleOnOpenChange(false)
        }
      }
    }

    if (resolvedOpenState && attachToCursor) {
      document.addEventListener("mousemove", handleMouseMove)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
    }
  }, [resolvedOpenState, attachToCursor, handleOnOpenChange])

  if (disabled || !content) {
    return <>{children}</>
  }

  return (
    <TooltipPrimitive.Root
      defaultOpen={defaultOpen}
      delay={delay}
      hoverable={hoverable}
      open={resolvedOpenState}
      trackCursorAxis={attachToCursor ? "both" : undefined}
      onOpenChange={handleOnOpenChange}
    >
      <TooltipPrimitive.Trigger
        className={classNames(!disabled && cursorClassName)}
        ref={triggerRef}
        render={children}
        onPointerLeave={() => {
          if (attachToCursor) {
            handleOnOpenChange(false)
          }
        }}
      />
      <TooltipPrimitive.Portal container={container}>
        <TooltipPrimitive.Positioner
          align={align}
          alignOffset={alignOffset}
          anchor={anchor}
          className={classNames(
            "z-tooltip",
            disablePointerEvents ? "pointer-events-none" : undefined,
          )}
          side={side}
          sideOffset={sideOffset}
        >
          <TooltipPrimitive.Popup
            className={classNames(
              tooltipVariants({ animation, className: "p-2" }),
              className,
            )}
            ref={ref}
            role="tooltip"
            style={style}
          >
            {content}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
})

export const Tooltip = Object.assign(TooltipBase, {
  Label: TooltipLabel,
})
